import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { setMeta } from '../utils/seo';
import domainConfig from '../data/domainConfig';
import dailySeoContent from '../data/dailySeoContent';

function Home() {
  // 获取当前域名配置，实现多域名差异化展示
  const currentDomain = process.env.REACT_APP_WEBSITE_DOMAIN || window.location.hostname;
  // 简单处理 www. 前缀
  const cleanDomain = currentDomain.replace(/^www\./, '');
  // 查找配置，优先匹配完整域名，其次匹配主域名，最后使用 default
  const config = domainConfig[currentDomain] || 
                 domainConfig[`www.${cleanDomain}`] || 
                 domainConfig[cleanDomain] || 
                 domainConfig['default'];
  const dailyFocusKeywords = Array.isArray(dailySeoContent.focusKeywords) ? dailySeoContent.focusKeywords : [];
  const dailyFaqEntries = Array.isArray(dailySeoContent.faqEntries) ? dailySeoContent.faqEntries : [];
  const dailyKeywordMeta = dailySeoContent.metaKeywords || '';
  const pilotTitleByDomain = {
    'zjfhzs.cn': 'HHpoker新手入门首页｜规则讲解与下载指南',
    'jshstx.com': 'HHpoker极速线路入口｜低延迟下载与安装教程',
    'xsdstudio.com': 'HHpoker合伙人合作入口｜代理政策与下载通道'
  };
  const homeTitle = pilotTitleByDomain[cleanDomain] || config.title || 'HHpoker官网入口｜官方访问、下载与安装指南';
  const homeDescription = config.description || 'HHpoker官网提供官方访问入口、最新版下载、iOS与安卓安装说明、俱乐部加入及客服指引，帮助新用户快速完成下载安装与常见问题处理。';
  const crawlPilotDomains = new Set(['zjfhzs.cn', 'jshstx.com', 'xsdstudio.com']);
  const isCrawlPilotDomain = crawlPilotDomains.has(cleanDomain);
  const crawlEntryLinks = [
    { path: '/download/', label: '官方下载入口与版本更新' },
    { path: '/install-guide/', label: '安装指引与常见错误处理' },
    { path: '/guide/', label: '新手规则与进阶策略教程' },
    { path: '/clubs/', label: '俱乐部加入与活跃局筛选' },
    { path: '/contact/', label: '客服支持与问题提交流程' },
    { path: '/hhpoker-latest-download/', label: '最新版下载专题页' },
    { path: '/hhpoker-install-guide/', label: '安装教程专题页' },
    { path: '/hhpoker-club-id/', label: '俱乐部 ID 与邀请码指南' },
    { path: '/hhpoker-contact-service/', label: '客服联络专题页' },
    { path: '/guide/detail', label: '深度攻略详情页' }
  ];
  const crossDomainBridgeMap = {
    'fishpt.com': [
      { href: 'https://zjfhzs.cn/', label: '新手规则专题（zjfhzs.cn）' },
      { href: 'https://donghaiedu.com/download', label: '新版下载入口（donghaiedu.com）' }
    ],
    'lgysjpj.com': [
      { href: 'https://ggxhu.com/download', label: '官方下载入口（ggxhu.com）' },
      { href: 'https://zjfhzs.cn/guide', label: '入门教程合集（zjfhzs.cn）' }
    ],
    'ruxinkeji.com': [
      { href: 'https://donghaiedu.com/', label: '官网入口备链（donghaiedu.com）' },
      { href: 'https://jshstx.com/download', label: '低延迟下载页（jshstx.com）' }
    ]
  };
  const crossDomainBridgeLinks = crossDomainBridgeMap[cleanDomain] || [];

  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      easing: 'ease-out-cubic',
    });
    // SEO 优化：针对百度移动端，强化核心关键词权重
    setMeta({
      title: homeTitle,
      description: homeDescription,
      keywords: `HHpoker官网入口, HHpoker官方访问, HHpoker下载, HHpoker安装指南, HHpoker俱乐部, HHpoker客服, ${dailyKeywordMeta}`,
      url: window.location.origin + '/',
      robots: 'index,follow',
      image: window.location.origin + '/images/banner.jpg',
      jsonLd: {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "MobileApplication",
            "name": "HHpoker",
            "url": window.location.origin,
            "logo": window.location.origin + "/images/logo.png",
            "description": config.description
          },
          {
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "HHpoker官网入口在哪里？",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "可通过本站首页进入 HHpoker 官网入口，并跳转到官方下载、俱乐部推荐和安装教程页面。"
                }
              },
              {
                "@type": "Question",
                "name": "HHpoker下载支持哪些手机？",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "当前支持 iOS 与 Android 双端，建议通过官方下载页获取最新版本安装包。"
                }
              },
              {
                "@type": "Question",
                "name": "德州扑克平台怎么选？",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "建议先看平台稳定性、下载与安装路径是否清晰，再看客服响应和新手教程是否完善。"
                }
              },
              {
                "@type": "Question",
                "name": "hhpoker俱乐部id怎么找？",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "可先查看俱乐部ID专题页，按活跃度、局级匹配和客服响应筛选，再按引导加入。"
                }
              },
              {
                "@type": "Question",
                "name": "hhpoker最新版本下载安装怎么做？",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "建议通过官方下载入口获取最新包体，按iOS或Android教程完成安装与权限设置。"
                }
              },
              {
                "@type": "Question",
                "name": "hhpoker苹果手机怎么下载？",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "从官方下载页选择iOS下载，安装后到系统设置完成证书信任即可。"
                }
              },
              ...dailyFaqEntries.map((item) => ({
                "@type": "Question",
                "name": item.name,
                "acceptedAnswer": item.acceptedAnswer
              }))
            ]
          }
        ]
      }
    });
  }, [config, homeTitle, homeDescription, dailyKeywordMeta, dailyFaqEntries]);

  return (
    <div style={{ background: '#141622', color: '#fff', overflowX: 'hidden', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
      
      {/* Hero Section */}
      <section className="hero-section" style={{
          position: 'relative',
          minHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          padding: '80px 20px 40px',
          background: `linear-gradient(to bottom, rgba(20,22,34,0.3), rgba(20,22,34,1)), url('/images/banner.jpg') center/cover no-repeat`
        }}
      >
        <div data-aos="fade-up" style={{maxWidth: '600px', width: '100%', zIndex: 2}}>
          <h1 style={{
            fontSize: 'clamp(2.5rem, 8vw, 4.5rem)', // 响应式字体
            background: 'linear-gradient(45deg, #efa765, #fff)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 800,
            marginBottom: '16px',
            lineHeight: 1.2
          }}>
            {config.h1}
          </h1>
          <p style={{
            fontSize: 'clamp(1rem, 4vw, 1.2rem)',
            color: '#d0d0d0',
            marginBottom: '40px',
            lineHeight: '1.6'
          }}>
            {config.subtext}
          </p>
          
          <div style={{display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center'}}>
            <Link to="/download" className="pulse-button" style={{
              display: 'block',
              width: '100%',
              maxWidth: '280px',
              background: 'linear-gradient(90deg, #efa765, #f5b97f)',
              color: '#1a1c29',
              padding: '16px 0',
              borderRadius: '50px',
              fontSize: '1.2rem',
              fontWeight: 700,
              textDecoration: 'none',
              boxShadow: '0 4px 20px rgba(239, 167, 101, 0.4)',
              textAlign: 'center'
            }}>
              获取游戏入口
            </Link>
            <span style={{fontSize: '0.8rem', color: '#666'}}>
              * 支持 iOS / Android 双端互通
            </span>
          </div>
        </div>
      </section>

      {/* 为什么选择我们 (New Section based on bymbeauty content) */}
      <section style={{ padding: '60px 20px', background: '#1a1c29', borderBottom: '1px solid #2a2d42' }}>
        <div data-aos="fade-up" style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontSize: '1.8rem', color: '#fff', marginBottom: '24px', fontWeight: 700 }}>
            为什么选择 <span style={{color: '#efa765'}}>HHpoker官方网站</span>?
          </h2>
          <p style={{ 
            fontSize: '1rem', 
            color: '#b0b0b0', 
            lineHeight: '1.8', 
            textAlign: 'justify', 
            marginBottom: '30px' 
          }}>
            HHpoker官方网站提供德扑圈APP官方下载、iOS/Android安装指引与最新版本更新说明，帮助您快速完成安装并稳定进入游戏。
            平台同步提供俱乐部推荐、规则攻略与在线客服支持，覆盖新手入门到进阶实战场景，打造更透明、更高效的一站式德州扑克服务入口。
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', flexWrap: 'wrap' }}>
             <div style={{ background: '#232533', padding: '10px 20px', borderRadius: '8px', border: '1px solid #333', color: '#efa765', fontSize: '0.9rem', fontWeight: 'bold' }}>
               🛡️ 24H 严格监管
             </div>
             <div style={{ background: '#232533', padding: '10px 20px', borderRadius: '8px', border: '1px solid #333', color: '#efa765', fontSize: '0.9rem', fontWeight: 'bold' }}>
               📊 专业数据分析
             </div>
             <div style={{ background: '#232533', padding: '10px 20px', borderRadius: '8px', border: '1px solid #333', color: '#efa765', fontSize: '0.9rem', fontWeight: 'bold' }}>
               🤝 真实社交互动
             </div>
          </div>
        </div>
      </section>

      <section style={{ padding: '28px 20px', background: '#202334', borderBottom: '1px solid #2a2d42' }}>
        <div style={{ maxWidth: '860px', margin: '0 auto' }}>
          <h2 style={{ color: '#efa765', fontSize: '1.4rem', marginBottom: '8px' }}>每日关键词更新</h2>
          <p style={{ color: '#b8bfce', marginTop: 0, marginBottom: '12px', fontSize: '0.92rem' }}>
            更新时间：{dailySeoContent.updatedAt} | 当日主题：{dailySeoContent.topic}
          </p>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {dailyFocusKeywords.map((item, idx) => (
              <Link key={`${item.keyword}-${idx}`} to={item.path} style={{ color: '#fff', background: '#2b2e42', padding: '8px 12px', borderRadius: '20px', textDecoration: 'none' }}>
                {item.anchorText || item.keyword}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {isCrawlPilotDomain && (
        <section style={{ padding: '28px 20px', background: '#1a1e30', borderBottom: '1px solid #2a2d42' }}>
          <div style={{ maxWidth: '860px', margin: '0 auto' }}>
            <h2 style={{ color: '#efa765', fontSize: '1.35rem', marginBottom: '10px' }}>重点入口导航（抓取强化测试）</h2>
            <p style={{ color: '#b8bfce', marginTop: 0, marginBottom: '12px', fontSize: '0.92rem', lineHeight: 1.7 }}>
              当前域名处于抓取入口强化测试阶段。以下为高优先级专题入口，帮助搜索引擎更快发现下载、安装、教程、客服与俱乐部核心页面。
            </p>
            <ul style={{ margin: 0, paddingLeft: '18px', color: '#cfd3dc', lineHeight: 1.9 }}>
              {crawlEntryLinks.map((item) => (
                <li key={item.path}>
                  <Link to={item.path} style={{ color: '#efa765', textDecoration: 'none' }}>{item.label}</Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {crossDomainBridgeLinks.length > 0 && (
        <section style={{ padding: '24px 20px', background: '#161a2a', borderBottom: '1px solid #2a2d42' }}>
          <div style={{ maxWidth: '860px', margin: '0 auto' }}>
            <h2 style={{ color: '#efa765', fontSize: '1.25rem', marginBottom: '10px' }}>跨站入口推荐</h2>
            <p style={{ color: '#b8bfce', marginTop: 0, marginBottom: '10px', fontSize: '0.92rem' }}>
              以下入口为同主题站点的重点页面，便于搜索引擎跨站发现相关内容。
            </p>
            <ul style={{ margin: 0, paddingLeft: '18px', color: '#cfd3dc', lineHeight: 1.9 }}>
              {crossDomainBridgeLinks.map((item) => (
                <li key={item.href}>
                  <a href={item.href} style={{ color: '#efa765', textDecoration: 'none' }}>{item.label}</a>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <section style={{ padding: '28px 20px', background: '#171a28', borderBottom: '1px solid #2a2d42' }}>
        <div style={{ maxWidth: '860px', margin: '0 auto', lineHeight: 1.75 }}>
          <h2 style={{ color: '#efa765', fontSize: '1.35rem', marginBottom: '12px' }}>每日问答更新</h2>
          {dailyFaqEntries.slice(0, 3).map((item, idx) => (
            <div key={`${item.name}-${idx}`} style={{ marginBottom: idx === 2 ? 0 : '14px' }}>
              <p style={{ color: '#cfd3dc', marginBottom: '6px' }}><strong>Q：{item.name}</strong></p>
              <p style={{ color: '#aeb4c2', marginBottom: 0 }}>A：{item.acceptedAnswer.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section style={{ padding: '36px 20px', background: '#1a1c29', borderBottom: '1px solid #2a2d42' }}>
        <div style={{ maxWidth: '860px', margin: '0 auto', lineHeight: 1.75 }}>
          <h2 style={{ color: '#efa765', fontSize: '1.5rem', marginBottom: '12px' }}>HHpoker官网常见问题</h2>
          <p style={{ color: '#c5c7ce', marginBottom: '10px' }}><strong>Q：HHpoker官网和下载页有什么区别？</strong></p>
          <p style={{ color: '#9ea3b0', marginBottom: '10px' }}>A：官网页提供总入口与核心导航，下载页聚焦 iOS/安卓安装和版本更新说明。</p>
          <p style={{ color: '#c5c7ce', marginBottom: '10px' }}><strong>Q：如何找到 HHpoker 最新下载 iOS/安卓版？</strong></p>
          <p style={{ color: '#9ea3b0', marginBottom: '0' }}>A：建议通过本站下载专区获取最新包体，并参考安装教程完成证书信任或安装权限设置。</p>
          <p style={{ color: '#c5c7ce', marginTop: '12px', marginBottom: '10px' }}><strong>Q：德州扑克平台怎么选？</strong></p>
          <p style={{ color: '#9ea3b0', marginBottom: '0' }}>A：优先选择下载入口清晰、安装流程完整、客服响应及时且有新手教程的平台，能明显降低使用门槛。</p>
          <p style={{ color: '#c5c7ce', marginTop: '12px', marginBottom: '10px' }}><strong>Q：德州平台新手友好推荐应该看哪些点？</strong></p>
          <p style={{ color: '#9ea3b0', marginBottom: '10px' }}>A：建议优先看是否提供德扑平台入门指南、安装教程和客服答疑，这类德州平台使用门槛低，更适合新手起步。</p>
          <p style={{ color: '#c5c7ce', marginBottom: '10px' }}><strong>Q：德州平台稳定性对比要怎么做？</strong></p>
          <p style={{ color: '#9ea3b0', marginBottom: '10px' }}>A：可从访问连通性、下载成功率、安装成功率和客服处理时效做德州平台稳定性对比，再决定长期使用。</p>
          <p style={{ color: '#c5c7ce', marginBottom: '10px' }}><strong>Q：德扑平台移动端体验主要看什么？</strong></p>
          <p style={{ color: '#9ea3b0', marginBottom: '0' }}>A：重点看页面加载速度、按钮可触达性、下载与安装流程是否顺滑，以及常见问题是否有清晰引导。</p>
        </div>
      </section>

      <section style={{ padding: '36px 20px', background: '#151826', borderBottom: '1px solid #2a2d42' }}>
        <div style={{ maxWidth: '860px', margin: '0 auto', lineHeight: 1.8 }}>
          <h2 style={{ color: '#efa765', fontSize: '1.5rem', marginBottom: '12px' }}>高频检索问题补充</h2>
          <p style={{ color: '#9ea3b0', marginBottom: '12px' }}>以下为移动端用户常搜问题，均可在本站对应专题页完成下载、安装、俱乐部与客服处理流程。</p>
          <p style={{ color: '#c5c7ce', marginBottom: '8px' }}><strong>Q：hhpoker俱乐部id怎么找？</strong></p>
          <p style={{ color: '#9ea3b0', marginBottom: '8px' }}>A：进入 <Link to="/hhpoker-club-id" style={{ color: '#efa765' }}>hhpoker俱乐部id</Link> 专题，按活跃度与局级匹配筛选；可同步了解 hhpoker俱乐部、hhpoker美区id、德州俱乐部id 与 线上德州俱乐部 选择建议。</p>
          <p style={{ color: '#c5c7ce', marginBottom: '8px' }}><strong>Q：hhpoker俱乐部邀请码在哪里获取？</strong></p>
          <p style={{ color: '#9ea3b0', marginBottom: '8px' }}>A：可通过俱乐部页面与客服核验流程获取 hhpoker俱乐部邀请码，优先选择 华人德州俱乐部 或 海外德州俱乐部 中规则透明的圈子。</p>
          <p style={{ color: '#c5c7ce', marginBottom: '8px' }}><strong>Q：hhpoker最新版本下载安装怎么做？</strong></p>
          <p style={{ color: '#9ea3b0', marginBottom: '8px' }}>A：打开 <Link to="/hhpoker-latest-download" style={{ color: '#efa765' }}>hhpoker最新版下载</Link> 页面，按 hhpoker最新版本下载安装 指引完成。若是 手机德州扑克平台 新用户，也可从 德州游戏app下载 入口直达。</p>
          <p style={{ color: '#c5c7ce', marginBottom: '8px' }}><strong>Q：hhpoker苹果手机怎么下载？</strong></p>
          <p style={{ color: '#9ea3b0', marginBottom: '8px' }}>A：在下载页选择 hhpoker苹果下载，按 iOS 信任步骤安装；Android 用户可参考 hhpoker安卓下载 与 hhpoker安卓安装教程。</p>
          <p style={{ color: '#c5c7ce', marginBottom: '8px' }}><strong>Q：hhpoker无法安装怎么处理？</strong></p>
          <p style={{ color: '#9ea3b0', marginBottom: '8px' }}>A：先检查权限与证书，再按 <Link to="/hhpoker-install-guide" style={{ color: '#efa765' }}>hhpoker安装教程</Link> 处理；仍异常可通过 hhpoker客服联系方式 提交问题。</p>
          <p style={{ color: '#c5c7ce', marginBottom: '8px' }}><strong>Q：hhpoker官方入口与客服入口在哪？</strong></p>
          <p style={{ color: '#9ea3b0', marginBottom: '0' }}>A：官网首页即 hhpoker官方入口；如需安装或账号问题处理，请前往 <Link to="/hhpoker-contact-service" style={{ color: '#efa765' }}>hhpoker客服</Link> 页面。若你在找 扑克俱乐部下载 或 hhpoker注册教程，也可从该页获取导航。</p>
          <p style={{ color: '#c5c7ce', marginTop: '12px', marginBottom: '8px' }}><strong>Q：hhpoker官网地址和下载入口在哪看？</strong></p>
          <p style={{ color: '#9ea3b0', marginBottom: '8px' }}>A：可先查看 hhpoker官网地址 导航，再进入下载页处理 德州扑克下载、德扑平台下载、德扑下载入口、德扑下载官网 与 德扑下载最新版 等需求。</p>
          <p style={{ color: '#c5c7ce', marginBottom: '8px' }}><strong>Q：新手学习内容应该从哪里开始？</strong></p>
          <p style={{ color: '#9ea3b0', marginBottom: '8px' }}>A：建议先看 德州扑克新手教程 与 德州扑克新手规则，再学习 德州扑克入门教程，最后结合安装与下载流程完成上手。</p>
          <p style={{ color: '#c5c7ce', marginBottom: '8px' }}><strong>Q：俱乐部相关问题最常见有哪些？</strong></p>
          <p style={{ color: '#9ea3b0', marginBottom: '0' }}>A：用户常搜 hhpoker俱乐部id号有哪些、hhpoker俱乐部客服电话、hhpoker俱乐部玩家群有哪些，以及 俱乐部邀请码怎么获取、俱乐部邀请码在哪填、俱乐部id加入方法；可在俱乐部专题页与客服页按步骤处理。</p>
        </div>
      </section>

      {/* 优势展示 (仿移动端卡片布局) */}
      <section style={{ padding: '40px 20px', background: '#141622' }}>
        <h2 style={{ fontSize: '1.8rem', color: '#fff', marginBottom: '30px', textAlign: 'center', fontWeight: 700 }}>
          <span style={{color: '#efa765'}}>HHpoker</span> 核心优势
        </h2>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
          {/* Card 1: 公平公正 */}
          <div data-aos="fade-up" style={{ background: '#1f2233', borderRadius: '16px', padding: '20px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
            <div style={{ background: 'rgba(239, 167, 101, 0.1)', padding: '12px', borderRadius: '50%', marginBottom: '12px', width: '56px', height: '56px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="#efa765"><path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/></svg>
            </div>
            <h3 style={{ fontSize: '1rem', color: '#fff', marginBottom: '6px' }}>信誉靠谱</h3>
            <p style={{ fontSize: '0.8rem', color: '#888', margin: 0 }}>多年行业经验，资深客服财务及风控运营团队</p>
          </div>

          {/* Card 2: 卓越体验 -> 活动奖励 */}
          <div data-aos="fade-up" data-aos-delay="100" style={{ background: '#1f2233', borderRadius: '16px', padding: '20px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
            <div style={{ background: 'rgba(239, 167, 101, 0.1)', padding: '12px', borderRadius: '50%', marginBottom: '12px', width: '56px', height: '56px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="#efa765"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg>
            </div>
            <h3 style={{ fontSize: '1rem', color: '#fff', marginBottom: '6px' }}>活动丰富</h3>
            <p style={{ fontSize: '0.8rem', color: '#888', margin: 0 }}>新人上桌、牌型奖励、金币赠送，福利多多</p>
          </div>

          {/* Card 3: 数据分析 -> 级别全面 */}
          <div data-aos="fade-up" data-aos-delay="200" style={{ background: '#1f2233', borderRadius: '16px', padding: '20px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
            <div style={{ background: 'rgba(239, 167, 101, 0.1)', padding: '12px', borderRadius: '50%', marginBottom: '12px', width: '56px', height: '56px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="#efa765"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/></svg>
            </div>
            <h3 style={{ fontSize: '1rem', color: '#fff', marginBottom: '6px' }}>级别全面</h3>
            <p style={{ fontSize: '0.8rem', color: '#888', margin: 0 }}>大小全级别覆盖优质牌局，提供多样选择</p>
          </div>

           {/* Card 4: 玩法丰富 */}
          <div data-aos="fade-up" data-aos-delay="300" style={{ background: '#1f2233', borderRadius: '16px', padding: '20px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
            <div style={{ background: 'rgba(239, 167, 101, 0.1)', padding: '12px', borderRadius: '50%', marginBottom: '12px', width: '56px', height: '56px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="#efa765"><path d="M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58.55 0 1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41 0-.55-.23-1.06-.59-1.42zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 4 6.33 5.5 7z"/></svg>
            </div>
            <h3 style={{ fontSize: '1rem', color: '#fff', marginBottom: '6px' }}>五星服务</h3>
            <p style={{ fontSize: '0.8rem', color: '#888', margin: 0 }}>客服财务24H实时响应，全方位贴心服务</p>
          </div>
        </div>
      </section>

      {/* 游戏丰富度展示 */}
      <section style={{ padding: '40px 20px', background: '#1a1c29', borderTop: '1px solid #2a2d42' }}>
         <div data-aos="fade-right" style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '30px'}}>
             <div>
                <h3 style={{fontSize: '1.5rem', fontWeight: 700, margin: '0 0 8px 0', color: '#fff'}}>
                   精益求精<br/>
                   <span style={{color: '#efa765', fontSize: '1.2rem'}}>全方位竞技体验</span>
                </h3>
                <div style={{height: '4px', width: '60px', background: '#efa765', borderRadius: '2px'}}></div>
             </div>
             {/* 可以放一个小插图 */}
             <div style={{fontSize: '3rem'}}>♠️</div>
         </div>
         
         <div className="game-modes-list" style={{display: 'flex', flexDirection: 'column', gap: '20px'}}>
            <div data-aos="fade-up" style={{display: 'flex', alignItems: 'center'}}>
               <div style={{width: '40px', height: '40px', background: '#333', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '16px', color:'#efa765', fontWeight:'bold'}}>NLH</div>
               <div>
                 <h4 style={{margin: '0 0 4px 0', fontSize: '1.1rem'}}>国际标准策略竞技</h4>
                 <p style={{margin: 0, fontSize: '0.9rem', color: '#888'}}>全球最流行的策略玩法，战术与勇气的较量</p>
               </div>
            </div>
            <div data-aos="fade-up" data-aos-delay="100" style={{display: 'flex', alignItems: 'center'}}>
               <div style={{width: '40px', height: '40px', background: '#333', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '16px', color:'#efa765', fontWeight:'bold'}}>PLO</div>
               <div>
                 <h4 style={{margin: '0 0 4px 0', fontSize: '1.1rem'}}>奥马哈 (Omaha)</h4>
                 <p style={{margin: 0, fontSize: '0.9rem', color: '#888'}}>更刺激的底池与成牌概率，体验心跳加速</p>
               </div>
            </div>
            <div data-aos="fade-up" data-aos-delay="200" style={{display: 'flex', alignItems: 'center'}}>
               <div style={{width: '40px', height: '40px', background: '#333', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '16px', color:'#efa765', fontWeight:'bold'}}>6+</div>
               <div>
                 <h4 style={{margin: '0 0 4px 0', fontSize: '1.1rem'}}>短牌 (Short Deck)</h4>
                 <p style={{margin: 0, fontSize: '0.9rem', color: '#888'}}>去除2-5的小牌，大牌频出，节奏紧凑</p>
               </div>
            </div>
             <div data-aos="fade-up" data-aos-delay="300" style={{display: 'flex', alignItems: 'center'}}>
               <div style={{width: '40px', height: '40px', background: '#333', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '16px', color:'#efa765', fontWeight:'bold'}}>Allin</div>
               <div>
                 <h4 style={{margin: '0 0 4px 0', fontSize: '1.1rem'}}>大菠萝 (Pusoy)</h4>
                 <p style={{margin: 0, fontSize: '0.9rem', color: '#888'}}>特殊牌型与极速对决，摆牌技巧的考验</p>
               </div>
            </div>
         </div>
      </section>

      {/* 战队/俱乐部展示 */}
      <section style={{ 
        padding: '60px 20px', 
        background: '#1a1c29', 
        textAlign: 'center' 
      }}>
        <div data-aos="zoom-in" style={{background: 'linear-gradient(135deg, #2a2d42 0%, #1a1c29 100%)', borderRadius: '20px', padding: '30px 20px', border: '1px solid #333'}}>
          <h2 style={{ fontSize: '1.5rem', color: '#efa765', marginBottom: '16px' }}>
            精选优质战队
          </h2>
          <p style={{color: '#aaa', fontSize: '0.9rem', marginBottom: '24px'}}>
             加入高活跃度战队，与大神同台切磋<br/>
             获取更多赛事资讯与技巧指导
          </p>
          
          <div style={{ maxWidth: '400px', margin: '0 auto 30px auto', borderRadius: '12px', overflow: 'hidden' }}>
            <img src="/images/union.png" alt="战队展示" style={{ width: '100%', display: 'block' }} />
          </div>

          <Link to="/clubs" style={{
            display: 'inline-block',
            padding: '12px 36px',
            border: '1px solid #efa765',
            color: '#efa765',
            borderRadius: '50px',
            fontSize: '1rem',
            textDecoration: 'none',
          }}>
            查看战队列表
          </Link>
        </div>
      </section>

      {/* 底部客服引导 */}
      <section style={{ padding: '60px 20px 40px', background: '#0c0909', textAlign: 'center' }}>
         <h3 style={{ color: '#fff', fontSize: '1.4rem', marginBottom: '20px' }}>遇到问题？</h3>
         <p style={{ color: '#888', marginBottom: '30px', fontSize: '0.9rem' }}>
           如需帮助或有任何疑问，请联系我们的24小时客服团队。<br/>
           我们保障您的隐私与数据安全。
         </p>
         
         <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
            <div style={{display: 'flex', alignItems: 'center', color: '#efa765', fontSize: '1.1rem', background: '#1a1c29', padding: '12px 24px', borderRadius: '8px', width: '100%', maxWidth: '300px', justifyContent: 'center'}}>
               <span style={{marginRight: '10px'}}>QQ客服:</span>
               <span style={{fontWeight: 'bold'}}>2222692</span>
            </div>
            <div style={{display: 'flex', alignItems: 'center', color: '#efa765', fontSize: '1.1rem', background: '#1a1c29', padding: '12px 24px', borderRadius: '8px', width: '100%', maxWidth: '300px', justifyContent: 'center'}}>
               <span style={{marginRight: '10px'}}>三条号:</span>
               <span style={{fontWeight: 'bold'}}>king33</span>
            </div>
         </div>
         
         <div style={{ marginTop: '50px', color: '#444', fontSize: '0.75rem' }}>
            Copyright © 2026 HH Games. All Rights Reserved.
         </div>
      </section>
    </div>
  );
}

export default Home;
